import * as React from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimerIcon from '@mui/icons-material/Timer';
import PlaceIcon from '@mui/icons-material/Place';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import { AXIOS_API_INSTANCE, all_locations_object } from '../Constants'
import { logout, send_authorized_request } from '../Constants/cognito';
import { Autocomplete, Badge, Box, Chip, CircularProgress, Collapse, debounce, Divider, ListItem, Menu, MenuItem, TextField, Typography, useMediaQuery } from '@mui/material';
import { cognitoUserPool } from "../Constants/cognito"
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { useTrackedDarkMode, useTrackedLocation, useTrackedNotificationsUnread, useTrackedTheme, useTrackedUser, useTrackedUserOpenServiceOrderWorkHistory } from './UserState';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import UserAvatar from './UserAvatar';
import ChangePasswordModal from './ChangePasswordModal';
import ConfigureMFA from './ConfigureMFA';
import { LoadingBackdropIndeterminate } from './LoadingBackdrop';
import { useUsers } from './ReactQuery/QueryUsers';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import UserNotificationsPreferencesModal from './UserNotificationsPreferencesModal';
import { MyFeedbackButton } from '../sentry_instrument';
import { ExpandMore } from '../Utils/ExpandMore';

const CURRENT_TAB_FRIENDLY_NAMES = {
    '/dashboard': 'Dashboard',
    '/login': 'Login',
    '/users': 'Users',
    '/customers': 'Customers',
    '/locations': 'Locations',
    '/reports': 'Reports',
    '/client': 'Client Configuration',
    '/rentalconfigs': 'Rental Configurations',
    '/rentalitems': 'Rental Items',
    '/reserve': 'Reserve Items',
    '/contracts': 'Contracts',
    '/contracts/:id': 'Contract',
    '/newserviceorder': 'New Service Order',
    '/serviceorders': 'Service Orders',
    '/serviceorders/:id': 'Service Order',
    '/servicekits': 'Service Kits',
    '/serviceorderitemtypes': 'Service Item Types',
    '/serviceorderfees': 'Service Order Fees',
    '/serviceorderparts': 'Service Order Parts',
    '/serviceorderwarrantyclaims': 'Service Order Warranty Claims',
    '/servicetechnicianhistory': 'Service Technician History',
    '/inventoryitems': 'Inventory Items'
}

const notificationTime = (n) => {
    const minutes = dayjs().diff(dayjs.utc(n.date_created), 'minutes')
    if (minutes < 1) {
        return `now`
    } else if (minutes < 60) {
        return `${minutes}m`
    } else if (minutes < (60 * 24)) {
        return `${Math.floor(minutes / 60)}h`
    } else {
        return `${Math.floor(minutes / (60 * 24))}d`
    }
}

const timerTime = (currentTime, t) => {
    const minutes = currentTime.diff(dayjs.utc(t), 'minutes')
    if (minutes < 60) {
        return `${minutes}m`
    } else {
        return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`
    }
}

function Notification({ n, showDivider }) {
    const [expanded, setExpanded] = React.useState(false);
    return (
        <Box sx={{ display: 'content', width: '100%' }}>
            <Box sx={{ textWrap: 'auto', pb: '3px', pt: '3px', minHeight: '55px', alignContent: 'center', width: '100%' }}>
                <span style={{ fontSize: '0.9rem' }}><b>{n.group}</b>{n.otherNotifications?.length > 0 && <Chip label={n.otherNotifications?.length + 1} size="small" sx={{ ml: 1 }}></Chip>}</span>
                {n.otherNotifications?.length > 0 &&
                    <ExpandMore
                        expand={expanded}
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setExpanded(!expanded) }}
                        aria-expanded={expanded}
                        aria-label="show all notifications"
                    >
                        <ExpandMoreIcon />
                    </ExpandMore>
                }
                <br />
                <span style={{ fontSize: '0.8rem' }}>{n.text}</span>
            </Box>
            <Box
                sx={{ position: 'absolute', right: '14px', top: '24px', borderRadius: '50%', bgcolor: 'info.main', minWidth: '10px', width: '10px', height: '10px', ml: 2, visibility: (!n.read || (n.otherNotifications ?? []).some((x) => !x.read)) ? 'visible' : 'hidden' }}>
            </Box>
            <Box sx={{ position: 'absolute', right: '14px', top: '4px', fontSize: '12px' }}>
                <span>{notificationTime(n)}</span>
            </Box>
            {n.otherNotifications?.length > 0 &&
                <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                    {n.otherNotifications.map((n2, idx) => (
                        <Box key={`${n2.id}-${idx}`} sx={{ position: 'relative' }}>
                            <Box sx={{ textWrap: 'auto', pb: '3px', pt: '3px', minHeight: '55px', alignContent: 'center', width: '90%' }}>
                                <span style={{ fontSize: '0.8rem' }}>{n2.text}</span>
                            </Box>
                            <Box
                                sx={{ position: 'absolute', right: '0px', top: '24px', borderRadius: '50%', bgcolor: 'info.main', minWidth: '10px', width: '10px', height: '10px', ml: 2, visibility: !n2.read ? 'visible' : 'hidden' }}>
                            </Box>
                            <Box sx={{ position: 'absolute', right: '0px', top: '4px', fontSize: '12px' }}>
                                <span>{notificationTime(n2)}</span>
                            </Box>
                        </Box>
                    ))}
                </Collapse>
            }
            {showDivider &&
                <Divider sx={{ position: 'absolute', 'bottom': 0, width: '95%', ml: -1, }} aria-hidden="true" />
            }
        </Box>
    )
}

function Header({ onDrawerToggle, currenttab, mfaStatus, setMfaStatus }) {
    const { enqueueSnackbar } = useSnackbar();
    const [my_user, set_my_user] = useTrackedUser();
    const [openServiceOrderWorkHistory] = useTrackedUserOpenServiceOrderWorkHistory();
    const [notificationsUnread, setNotificationsUnread] = useTrackedNotificationsUnread();
    const [notifications, setNotifications] = React.useState({ rows: [], total_count: 0, count_found: 0, oldest: undefined });
    const [notificationsLoading, setNotificationsLoading] = React.useState(true);
    const [selectedLocation, setSelectedLocation] = useTrackedLocation();
    const [darkMode,] = useTrackedDarkMode();
    const [theme,] = useTrackedTheme();
    const { refetch: refetchUsers } = useUsers();
    const [notificationsMenuAnchorElement, setNotificationsMenuAnchorElement] = React.useState(null);
    const [notificationsMenuItemOpen, setNotificationsMenuItemOpen] = React.useState(false);
    const [notificationOptionsAnchorEl, setNotificationOptionsAnchorEl] = React.useState(null)
    const [locationsMenuAnchorElement, setLocationsMenuAnchorElement] = React.useState(null);
    const [locationsMenuItemOpen, setLocationsMenuItemOpen] = React.useState(false);
    const [userOptionsMenuAnchorElement, setUserOptionsMenuAnchorElement] = React.useState(null);
    const [userOptionsMenuItemOpen, setUserOptionsMenuItemOpen] = React.useState(false);
    const [uploadingNewAvatar, setUploadingNewAvatar] = React.useState(false)
    const [mfaModal, setMfaModal] = React.useState({ open: false, secretCode: null });
    const [userNotificationsPreferencesModalOpen, setUserNotificationsPreferencesModalOpen] = React.useState(false)
    const [changePasswordModal, setChangePasswordModal] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(dayjs.utc()); // Used to refresh the timer on the service order clock
    const navigate = useNavigate();
    const logout_redirect = () => {
        logout()
    }
    const isXsDown = useMediaQuery(theme.breakpoints.down('sm'));
    const my_darkMode = darkMode
    const handleLocationChange = (event, new_loc) => {
        localStorage.setItem(`selected_location`, new_loc.id)
        setSelectedLocation(new_loc)
    }
    const openLocationsMenu = (event) => {
        setLocationsMenuAnchorElement(event.currentTarget)
        setLocationsMenuItemOpen(true)
    }
    const openNotificationsMenu = (event) => {
        setNotificationsMenuAnchorElement(event.currentTarget)
        setNotificationsMenuItemOpen(true)
        getNotifications()
    }
    const openUserOptionsMenu = (event) => {
        setUserOptionsMenuAnchorElement(event.currentTarget)
        setUserOptionsMenuItemOpen(true)
    }

    const uploadNewAvatar = (event) => {
        if (!event.target.files || event.target.files.length === 0) {
            return;
        }
        const file = event.target.files[0];
        setUploadingNewAvatar(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`my_user/image`, { image_name: file.name },).then((response) => {
                console.log(response)
                var bodyFormData = new FormData();
                for (const key in response.data.data.fields) {
                    bodyFormData.append(key, response.data.data.fields[key])
                }
                bodyFormData.append('file', file)
                axios.post(response.data.data.url, bodyFormData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((s3_response) => {
                    console.log(s3_response)
                    enqueueSnackbar('Successfully updated profile picture', { variant: 'success' })
                    setUploadingNewAvatar(false)
                    set_my_user({ ...my_user, ...response.data.user })
                    refetchUsers()
                }).catch((error) => {
                    setUploadingNewAvatar(false)
                    enqueueSnackbar(`Failed to update profile picture <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            }).catch((error) => {
                setUploadingNewAvatar(false)
                enqueueSnackbar(`Failed to update profile picture <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    const change_password = () => {
        setChangePasswordModal(true)
    }
    const close_change_password_modal = (changes) => {
        if (changes) {

        }
        setChangePasswordModal(false)
    }
    const close_mfa_modal = (changes) => {
        if (changes) {
            setMfaStatus(true)
        }
        setMfaModal({ open: false, secretCode: `` })
    }
    const disable_mfa = () => {
        const user = cognitoUserPool.getCurrentUser()
        if (user != null) {
            user.getSession((err, session) => {
                if (err) {
                    logout()
                    return;
                }
                user.setSignInUserSession(session)
                var totpMfaSettings = {
                    PreferredMfa: false,
                    Enabled: false,
                };
                user.setUserMfaPreference(null, totpMfaSettings, function (err, result) {
                    if (err) {
                        alert(err.message || JSON.stringify(err));
                    } else {
                        enqueueSnackbar("MFA Updated Successfully", { variant: 'success' })
                        setMfaStatus(false)
                    }
                    console.log('MFA call result ' + result);
                });
            })
        } else {
            logout();
            return
        }
    }
    const enable_mfa = () => {
        const user = cognitoUserPool.getCurrentUser()
        if (user != null) {
            user.getSession((err, session) => {
                if (err) {
                    logout()
                    return;
                }
                user.setSignInUserSession(session)
                user.associateSoftwareToken({
                    onFailure: (err) => {
                        console.error(err);
                        enqueueSnackbar('Failed to configure MFA', { variant: 'error' })
                    },
                    associateSecretCode: (secretCode) => {
                        setMfaModal({ open: true, secretCode: `otpauth://totp/ProfitMax Pro Suite:${user.email}?secret=${secretCode}` })
                    }
                });
            })
        } else {
            logout();
            return
        }
    }

    const location_options = (my_user?.locations || []).map((loc) => {
        return <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
    })
    location_options.push((<MenuItem key="all" value="all">All Locations</MenuItem>))

    const getNotificationsUnread = () => {
        send_authorized_request(() => {
            AXIOS_API_INSTANCE.get(`notifications/unread`).then((response) => {
                setNotificationsUnread(response.data.data.total_count ?? 0)
            }).catch((error) => {
            });
        })
        debouncedgetNotificationsUnread()
    }
    const getNotifications = () => {
        send_authorized_request(() => {
            getNotificationsUnread()
            setNotificationsLoading(true)
            AXIOS_API_INSTANCE.get(`notifications`, {
                params: {
                    page_num: 0,
                    page_size: 50,
                    order_by: 'date_created',
                    order_by_direction: 'desc',
                    filters: notifications.rows.length > 0 ? JSON.stringify({ 'date_created.lt': notifications.oldest }) : []
                }
            }).then((response) => {
                const groups = {};
                const my_rows = [];
                [...notifications.rows, ...response.data.data].forEach((n) => {
                    if (groups[n.group] === undefined) {
                        groups[n.group] = my_rows.length
                        my_rows.push({ ...n, otherNotifications: n.otherNotifications ?? [] })
                    } else {
                        my_rows[groups[n.group]].otherNotifications.push(n)
                    }
                });
                setNotifications({
                    rows: my_rows,
                    total_count: notifications.total_count > 0 ? notifications.total_count : response.data.total_count,
                    count_found: (notifications.count_found ?? 0) + response.data.data.length,
                    oldest: response.data.data.length > 0 ? response.data.data[response.data.data.length - 1].date_created : notifications.oldest
                })
                setNotificationsLoading(false)
            }).catch((error) => {
                setNotificationsLoading(false)
            });
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedgetNotificationsUnread = React.useMemo(() => debounce(() => getNotificationsUnread(), 1000 * 60 * 5), []); // 5 minutes

    const clickNotification = (n) => {
        if (!n.read) {
            send_authorized_request(() => {
                AXIOS_API_INSTANCE.post(`notifications/${encodeURIComponent(n.group)}/read_group`).then((response) => {
                    getNotificationsUnread()
                    setNotifications({ rows: [], total_count: 0, count_found: 0, oldest: undefined })
                }).catch((error) => {
                });
            })
        }
        if (n.link) {
            navigate(n.link)
        }
        setNotificationsMenuItemOpen(false)
    }

    const readAllNotifications = () => {
        send_authorized_request(() => {
            AXIOS_API_INSTANCE.post(`notifications/readall`).then((response) => {
                setNotifications({
                    rows: notifications.rows.map((n) => {
                        return {
                            ...n, read: true, otherNotifications: n.otherNotifications.map((n2) => { return { ...n2, read: true } })
                        }
                    }),
                    total_count: notifications.total_count,
                    count_found: notifications.count_found,
                    oldest: notifications.oldest
                })
                setNotificationsUnread(0)
            }).catch((error) => {
            });
        })
    }

    React.useEffect(() => {
        getNotificationsUnread()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentTime(dayjs.utc());
        }, 3000); // Update every 3 seconds

        return () => clearInterval(intervalId);
    }, []);

    return (
        (<React.Fragment>
            <LoadingBackdropIndeterminate
                open={uploadingNewAvatar}
                text={"Updating Profile"}
            />
            <AppBar color="brandcolor" enableColorOnDark position="sticky" elevation={0} sx={{ padding: '8px 0px 8px 0px' }}>
                <Toolbar>
                    <Grid container spacing={1} alignItems="center" wrap='nowrap' sx={{ width: '100%' }}>
                        <Grid sx={{ display: { md: 'none', sm: 'block', xs: 'block' } }}>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={onDrawerToggle}
                                edge="start"
                            >
                                <MenuIcon />
                            </IconButton>
                        </Grid>
                        {!isXsDown &&
                            <Grid size="auto">
                                {(my_user?.client?.image_path !== undefined && my_user?.client?.image_path !== null && my_user?.client?.image_path !== "") &&
                                    <Box component="img" alt={"Company Logo"} src={`/client/${my_user?.client?.id}/images/client_logo.png?update=${my_user?.client?.date_updated}`} sx={{ maxHeight: '50px', verticalAlign: 'middle' }} />
                                }
                            </Grid>
                        }
                        <Grid size="auto">
                            <Typography
                                color="inherit"
                                variant="h5"
                                component="h1"
                            >
                                {CURRENT_TAB_FRIENDLY_NAMES[currenttab]}
                            </Typography>
                        </Grid>
                        <Grid size="grow" />
                        {!isXsDown &&
                            <Grid sx={{ minWidth: '225px' }}>
                                <Autocomplete
                                    sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                                    className={`header-location-selector-${theme.palette.brandcolor.contrastText === '#fff' ? 'light' : 'dark'}`}
                                    color="brandcolor"
                                    getOptionLabel={(option) => `${option.name || ""}`}
                                    options={(my_user?.locations || []).toSorted((a, b) => a.name.localeCompare(b.name)).concat(all_locations_object)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    autoComplete
                                    includeInputInList
                                    disableClearable
                                    value={selectedLocation}
                                    noOptionsText="No Locations"
                                    onChange={handleLocationChange}
                                    renderInput={(params) => {
                                        return (<TextField {...params} label="Location" className={`header-location-selector'${theme.palette.brandcolor.contrastText === '#fff' ? 'light' : 'dark'}`} fullWidth autoComplete='off' />)
                                    }}
                                    renderOption={(props, option, { inputValue }) => {
                                        const matches = match(props.key, inputValue, { insideWords: true, findAllOccurrences: true });
                                        const parts = parse(props.key, matches);
                                        props.key = props.id

                                        return (
                                            <li {...props} >
                                                <div>
                                                    {parts.map((part, index) => (
                                                        <span
                                                            key={index}
                                                            style={{
                                                                fontWeight: part.highlight ? 700 : 400,
                                                            }}
                                                        >
                                                            {part.text}
                                                        </span>
                                                    ))}
                                                </div>
                                            </li>
                                        );
                                    }}
                                />
                            </Grid>
                        }
                        {isXsDown &&
                            <Grid>
                                <IconButton color="inherit" onClick={openLocationsMenu}>
                                    <PlaceIcon />
                                </IconButton>
                                <Menu
                                    id={'header-locations-menu'}
                                    anchorEl={locationsMenuAnchorElement}
                                    open={locationsMenuItemOpen}
                                    onClose={() => setLocationsMenuItemOpen(false)}
                                    slotProps={{
                                        list: {
                                            'aria-labelledby': 'basic-button',
                                        }
                                    }}
                                >
                                    <MenuItem divider disabled sx={{ opacity: 1 }} style={{ opacity: 1 }}>
                                        <span><b>{selectedLocation?.name}</b></span>
                                    </MenuItem>
                                    {(my_user?.locations || []).concat(all_locations_object).map((loc) => {
                                        if (loc.id === selectedLocation.id) {
                                            return undefined
                                        }
                                        return (
                                            <MenuItem onClick={() => { setSelectedLocation(loc); setLocationsMenuItemOpen(false) }} key={loc.id}>
                                                <span>{loc.name}</span>
                                            </MenuItem>
                                        )
                                    })}
                                </Menu>
                            </Grid>
                        }
                        <Grid>
                            <Tooltip title="Notifications">
                                <IconButton color="inherit" onClick={openNotificationsMenu}>
                                    <Badge badgeContent={notificationsUnread ?? 0} color="error">
                                        <NotificationsIcon />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                            <Menu
                                id={'header-user-options-menu'}
                                anchorEl={notificationsMenuAnchorElement}
                                open={notificationsMenuItemOpen}
                                onClose={() => { setNotificationsMenuItemOpen(false); setNotifications({ rows: [], total_count: 0, count_found: 0, oldest: undefined }) }}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            width: { xs: '300px', sm: '400px' },
                                        },
                                    },

                                    list: {
                                        'aria-labelledby': 'basic-button',
                                    }
                                }}>
                                <ListItem
                                    sx={{ justifyContent: 'space-between' }}>
                                    <Badge badgeContent={notificationsUnread ?? 0} color="error" max={999}>
                                        <Box sx={{ mt: 0.5, mb: 0.5 }}>
                                            <b>Notifications</b>
                                        </Box>
                                    </Badge>
                                    <IconButton
                                        aria-label="more"
                                        id="long-button"
                                        aria-controls={Boolean(notificationOptionsAnchorEl) ? 'long-menu' : undefined}
                                        aria-expanded={Boolean(notificationOptionsAnchorEl) ? 'true' : undefined}
                                        aria-haspopup="true"
                                        onClick={(e) => setNotificationOptionsAnchorEl(e.currentTarget)}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                        id="long-menu"
                                        anchorEl={notificationOptionsAnchorEl}
                                        open={Boolean(notificationOptionsAnchorEl)}
                                        onClose={() => setNotificationOptionsAnchorEl(null)}
                                        slotProps={{
                                            list: {
                                                'aria-labelledby': 'long-button',
                                            }
                                        }}
                                    >
                                        <MenuItem onClick={() => { readAllNotifications(); setNotificationOptionsAnchorEl(null) }}>
                                            Mark all as read
                                        </MenuItem>
                                    </Menu>
                                </ListItem>
                                <Divider />
                                {notifications.rows.map((n, idx) =>
                                    <MenuItem key={idx} onClick={() => clickNotification(n)} sx={{ width: '100%' }}>
                                        <Notification
                                            n={n}
                                            showDivider={idx !== notifications.rows.length - 1}
                                            clickNotification={clickNotification}
                                        />
                                    </MenuItem>
                                )}
                                {!notificationsLoading && notifications.rows.length === 0 &&
                                    <MenuItem disabled={true}>
                                        No Notifications
                                    </MenuItem>
                                }
                                {notificationsLoading &&
                                    <ListItem sx={{ justifyContent: 'center' }}>
                                        < Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                            <CircularProgress />
                                        </Box>
                                    </ListItem>
                                }
                                {!notificationsLoading && notifications.rows.length !== notifications.total_count &&
                                    <Divider />
                                }
                                {!notificationsLoading && notifications.count_found !== notifications.total_count &&
                                    <MenuItem disabled={notificationsLoading} onClick={notificationsLoading ? undefined : getNotifications}>
                                        <Box sx={{ pb: 1, pt: 1, textAlign: 'center', flex: 'auto', fontSize: '1.4em' }}>
                                            <span><RefreshIcon sx={{ mb: '-5px' }} /><b>Load More</b></span>
                                        </Box>
                                    </MenuItem>
                                }
                            </Menu>
                        </Grid>
                        {openServiceOrderWorkHistory &&
                            <Grid>
                                <Tooltip title="Running Service Order Work Timer">
                                    <IconButton color="error" onClick={() => navigate(`/ServiceOrders/${openServiceOrderWorkHistory.service_order_id}`)}>
                                        <Badge
                                            badgeContent={`${timerTime(currentTime, openServiceOrderWorkHistory.start_time)}`}
                                            color="error"
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'right',
                                            }}
                                            slotProps={{ badge: { sx: { bottom: -4 } } }}
                                        >
                                            <TimerIcon />
                                        </Badge>
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        }
                        <Grid>
                            <IconButton color="inherit" sx={{ p: 0.5 }} component='span' onClick={openUserOptionsMenu}>
                                <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    color="grey"
                                    badgeContent={<span><b>▼</b></span>}
                                >
                                    <UserAvatar user={my_user} />
                                </Badge>
                            </IconButton>
                            <Menu
                                id={'header-user-options-menu'}
                                anchorEl={userOptionsMenuAnchorElement}
                                open={userOptionsMenuItemOpen}
                                onClose={() => setUserOptionsMenuItemOpen(false)}
                                slotProps={{
                                    list: {
                                        'aria-labelledby': 'basic-button',
                                    }
                                }}
                            >
                                <MenuItem divider disabled sx={{ opacity: 1 }} style={{ opacity: 1 }}>
                                    <span><b>{my_user.name}</b></span>
                                </MenuItem>
                                <MenuItem onClick={null}>
                                    <input id="upload-avatar-pic" type="file" accept="image/*" hidden onChange={uploadNewAvatar} />
                                    <label htmlFor="upload-avatar-pic" style={{ cursor: 'inherit' }}>
                                        <span>Upload New Avatar</span>
                                    </label>
                                </MenuItem>
                                <MenuItem onClick={logout_redirect}>
                                    Sign Out
                                </MenuItem>
                                <MenuItem onClick={change_password}>
                                    Change Password
                                </MenuItem>
                                <MenuItem onClick={() => setUserNotificationsPreferencesModalOpen(true)}>
                                    Notification Preferences
                                </MenuItem>
                                {!mfaStatus &&
                                    <MenuItem onClick={enable_mfa}>
                                        Enable MFA
                                    </MenuItem>
                                }
                                {mfaStatus &&
                                    <MenuItem onClick={disable_mfa}>
                                        Disable MFA
                                    </MenuItem>
                                }
                                {/* TODO: Should not need to reload page... */}
                                <MenuItem onClick={() => { localStorage.setItem("darkMode", !my_darkMode); window.location.reload(); }}>
                                    {my_darkMode ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                    <Box sx={{ pl: 1 }}>Dark Mode</Box>
                                </MenuItem>
                                <MenuItem onClick={null}>
                                    Support
                                </MenuItem>
                                <MyFeedbackButton onClick={() => setUserOptionsMenuItemOpen(false)} />
                            </Menu>
                        </Grid>
                    </Grid>
                </Toolbar>
            </AppBar>
            <ChangePasswordModal
                open={changePasswordModal}
                closeModal={close_change_password_modal}
            />
            <ConfigureMFA
                open={mfaModal.open}
                secretCode={mfaModal.secretCode}
                closeModal={close_mfa_modal}
            />
            <UserNotificationsPreferencesModal
                open={userNotificationsPreferencesModalOpen}
                closeModal={() => setUserNotificationsPreferencesModalOpen(false)}
            />
        </React.Fragment>)
    );
}

Header.propTypes = {
    onDrawerToggle: PropTypes.func.isRequired,
};

export default Header;